import { ApiError, apiFetch } from "../client"
import { fromApi } from "../transformers"

export interface ReconciliationCandidate {
  statementRecordId: string
  source: string
  transactionReference: string
  amount: number
  currency: string
  paymentDate: string
  createdAt: string
}

export interface ReconciliationManualPaymentRow {
  id: string
  confirmationId: string
  clientId: string
  clientName: string
  loanId: string
  transactionReference: string
  amount: number
  currency: string
  paymentDate: string
  paymentMethod: string
  status: string
  rejectionReason: string | null
  requiresManualReview: boolean | number
  manualReviewReason: string | null
  createdAt: string
  matchedRepaymentId: string | null
  matchedStatementRecordId: string | null
  matchedStatementReference: string | null
  candidates: ReconciliationCandidate[]
}

export async function listManualPayments(params: {
  page?: number
  perPage?: number
  queue?: string
  status?: string
  search?: string
}) {
  const query = new URLSearchParams()
  query.set("page", String(params.page ?? 1))
  query.set("per_page", String(params.perPage ?? 20))

  if (params.queue) query.set("queue", params.queue)
  if (params.status) query.set("status", params.status)
  if (params.search) query.set("search", params.search)

  return apiFetch<{
    status: string
    data: ReconciliationManualPaymentRow[]
    meta: {
      page: number
      perPage: number
      totalItems: number
      totalPages: number
    }
  }>(`/reconciliation/manual-payments?${query.toString()}`)
}

export async function confirmManualPayment(
  confirmationId: string,
  body: { statementRecordId?: string; note?: string },
) {
  return apiFetch<{
    status: string
    data: {
      confirmationId: string
      repaymentId: string
      clientId: string
      loanId: string
      amount: number
      currency: string
      remainingBalance: number
    }
  }>(`/reconciliation/manual-payments/${confirmationId}/confirm`, {
    method: "POST",
    rawBody: body,
  })
}

export async function rejectManualPayment(confirmationId: string, rejectionReason: string) {
  return apiFetch<{
    status: string
    data: {
      confirmationId: string
      clientId: string | null
      loanId: string | null
      rejectionReason: string
    }
  }>(`/reconciliation/manual-payments/${confirmationId}/reject`, {
    method: "POST",
    rawBody: { rejectionReason },
  })
}

export async function uploadStatementImport(file: File) {
  const token = localStorage.getItem("session_token")
  const headers = new Headers()
  if (token) {
    headers.set("Authorization", `Bearer ${token}`)
  }

  const body = new FormData()
  body.set("file", file)

  const response = await fetch("/api/v1/reconciliation/statement-imports", {
    method: "POST",
    headers,
    body,
  })

  const isJson = response.headers.get("Content-Type")?.includes("application/json")
  const json = isJson ? fromApi<any>(await response.json()) : null

  if (!response.ok) {
    throw new ApiError(
      json?.error?.code ?? "UNKNOWN",
      json?.error?.message ?? "An unexpected error occurred.",
      response.status,
      json?.error?.details,
    )
  }

  return json as {
    status: string
    data: {
      statementImportId: string
      rowCount: number
    }
  }
}
